function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from 'react';
import { withRouter } from 'react-router';
import { hasTenantFeature } from '../../functions/tenant-features';
import { bucketCampaignsByType, totalsForRows, segmentBreakdown } from './aggregations';
import { deriveAnalysis } from './analysis';
import { NON_CTR_TYPES, SEGMENT_MARKETS } from './report-config';
import { resolveTitle, clean } from './title-resolver';
import { SkeletonGrid } from '../../components/skeleton';

// Drill-through: MI product rows open Product Detail (Phase A) when the
// tenant has the Territory/Rep surface (the sales endpoints behind it).
// Gated so tenants without territoryRep keep plain rows.
function productRowProps(history, p) {
  if (!hasTenantFeature('territoryRep') || !p || !p.productId) return {};
  return {
    className: 'clickable',
    title: `Open ${p.title || p.productId} product detail`,
    onClick: () => history.push({
      pathname: `/Territory/this_year/product/${encodeURIComponent(p.productId)}`,
      state: p.title ? {
        title: p.title
      } : undefined
    })
  };
}

// Section skeleton: same panel chrome as a real section, but the body
// is a pulsing skeleton grid. Used while a section's data is loading.
export function SectionSkeleton({
  title,
  rows = 6,
  cols = 4
}) {
  return /*#__PURE__*/React.createElement("section", {
    className: "mi-panel mi-panel-full",
    "aria-label": `${title} (loading)`
  }, /*#__PURE__*/React.createElement("header", {
    className: "mi-panel-header"
  }, /*#__PURE__*/React.createElement("h2", null, title)), /*#__PURE__*/React.createElement("div", {
    className: "mi-panel-body"
  }, /*#__PURE__*/React.createElement(SkeletonGrid, {
    rows: rows,
    cols: cols
  })));
}

// Section components for the report-style MI page. Each section is its
// own mini-panel (card chrome via mi-panel + mi-panel-full).
// Spend is never rendered (per design decision).

// --- formatting helpers ------------------------------------------------------

const fmt = {
  int: n => Number(n) < 0 ? '—' : Number(n || 0).toLocaleString('en-US'),
  // -1 = no forecast yet
  dec1: n => Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }),
  dec2: n => Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }),
  pct: n => `${Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}%`,
  money0: n => `$${Number(n || 0).toLocaleString('en-US', {
    maximumFractionDigits: 0
  })}`
};
function deltaCell(current, previous) {
  const c = Number(current) || 0;
  const p = Number(previous) || 0;
  if (p === 0) {
    if (c === 0) return /*#__PURE__*/React.createElement("span", {
      className: "mi-delta"
    }, "\u2014");
    return /*#__PURE__*/React.createElement("span", {
      className: "mi-delta mi-delta-up"
    }, "+\u221E%");
  }
  const pct = (c - p) / Math.abs(p) * 100;
  const sign = pct >= 0 ? '+' : '';
  const cls = pct >= 0 ? 'mi-delta-up' : 'mi-delta-down';
  return /*#__PURE__*/React.createElement("span", {
    className: `mi-delta ${cls}`
  }, sign, fmt.dec1(pct), "%");
}
function deltaCellPoints(current, previous, unit = 'pp') {
  const diff = (Number(current) || 0) - (Number(previous) || 0);
  const sign = diff >= 0 ? '+' : '';
  const cls = diff >= 0 ? 'mi-delta-up' : 'mi-delta-down';
  return /*#__PURE__*/React.createElement("span", {
    className: `mi-delta ${cls}`
  }, sign, fmt.dec2(diff), " ", unit);
}
const COMPARISON_LABEL = {
  previous_period: 'Previous',
  previous_year: 'Prior Year'
};
function prettifyCampaignName(name) {
  return String(name || '').replace(/_/g, ' ');
}

// Inline markdown: just handles **bold**.
function renderInlineMd(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return /*#__PURE__*/React.createElement("strong", {
        key: i
      }, p.slice(2, -2));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: i
    }, p);
  });
}

// --- All Markets Snapshot ----------------------------------------------------

export function AllMarketsSnapshot({
  campaignRows,
  placeIndex,
  prevCampaignRows,
  prevPlaceIndex,
  comparison,
  prevReady
}) {
  const totals = totalsForRows(campaignRows);
  const webViews = Object.values(placeIndex || {}).reduce((s, p) => s + (p.views || 0), 0);
  const webSessions = Object.values(placeIndex || {}).reduce((s, p) => s + (p.sessions || 0), 0);
  const webUsers = Object.values(placeIndex || {}).reduce((s, p) => s + (p.uniqueUsers || 0), 0);
  const prevTotals = totalsForRows(prevCampaignRows || []);
  const prevWebViews = Object.values(prevPlaceIndex || {}).reduce((s, p) => s + (p.views || 0), 0);
  const prevWebSessions = Object.values(prevPlaceIndex || {}).reduce((s, p) => s + (p.sessions || 0), 0);
  const prevWebUsers = Object.values(prevPlaceIndex || {}).reduce((s, p) => s + (p.uniqueUsers || 0), 0);
  const prevLabel = COMPARISON_LABEL[comparison] || 'Previous';
  return /*#__PURE__*/React.createElement("section", {
    className: "mi-panel mi-panel-full",
    "aria-label": "All Markets Snapshot"
  }, /*#__PURE__*/React.createElement("header", {
    className: "mi-panel-header"
  }, /*#__PURE__*/React.createElement("h2", null, "All Markets Snapshot")), /*#__PURE__*/React.createElement("div", {
    className: "mi-panel-body"
  }, /*#__PURE__*/React.createElement("table", {
    className: "mi-report-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null), /*#__PURE__*/React.createElement("th", null, "Cluster"), /*#__PURE__*/React.createElement("th", null, prevLabel), /*#__PURE__*/React.createElement("th", null, "\u0394"))), /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Total Impressions"), /*#__PURE__*/React.createElement("td", null, fmt.int(totals.impressions)), /*#__PURE__*/React.createElement("td", null, prevReady ? fmt.int(prevTotals.impressions) : '…'), /*#__PURE__*/React.createElement("td", null, prevReady ? deltaCell(totals.impressions, prevTotals.impressions) : '')), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Total Video Views"), /*#__PURE__*/React.createElement("td", null, fmt.int(totals.videoViews)), /*#__PURE__*/React.createElement("td", null, prevReady ? fmt.int(prevTotals.videoViews) : '…'), /*#__PURE__*/React.createElement("td", null, prevReady ? deltaCell(totals.videoViews, prevTotals.videoViews) : '')), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Total Clicks"), /*#__PURE__*/React.createElement("td", null, fmt.int(totals.clicks)), /*#__PURE__*/React.createElement("td", null, prevReady ? fmt.int(prevTotals.clicks) : '…'), /*#__PURE__*/React.createElement("td", null, prevReady ? deltaCell(totals.clicks, prevTotals.clicks) : '')), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Overall CTR (click-driven inventory)"), /*#__PURE__*/React.createElement("td", null, fmt.pct(totals.ctr)), /*#__PURE__*/React.createElement("td", null, prevReady ? fmt.pct(prevTotals.ctr) : '…'), /*#__PURE__*/React.createElement("td", null, prevReady ? deltaCellPoints(totals.ctr, prevTotals.ctr) : '')), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Geo-Targeted Website Views"), /*#__PURE__*/React.createElement("td", null, fmt.int(webViews)), /*#__PURE__*/React.createElement("td", null, prevReady ? fmt.int(prevWebViews) : '…'), /*#__PURE__*/React.createElement("td", null, prevReady ? deltaCell(webViews, prevWebViews) : '')), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Geo-Targeted Website Sessions"), /*#__PURE__*/React.createElement("td", null, fmt.int(webSessions)), /*#__PURE__*/React.createElement("td", null, prevReady ? fmt.int(prevWebSessions) : '…'), /*#__PURE__*/React.createElement("td", null, prevReady ? deltaCell(webSessions, prevWebSessions) : '')), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Geo-Targeted Website Unique Users"), /*#__PURE__*/React.createElement("td", null, fmt.int(webUsers)), /*#__PURE__*/React.createElement("td", null, prevReady ? fmt.int(prevWebUsers) : '…'), /*#__PURE__*/React.createElement("td", null, prevReady ? deltaCell(webUsers, prevWebUsers) : '')))), /*#__PURE__*/React.createElement("p", {
    className: "mi-report-note"
  }, "Geo-targeted website metrics reflect traffic from the configured market footprints only \u2014 a subset of the full-site analytics."), /*#__PURE__*/React.createElement("p", {
    className: "mi-report-note"
  }, /*#__PURE__*/React.createElement("strong", null, "Tracked Website Usage"), " sections count only visitors whose location could be verified via IP geolocation. Users with VPNs, anonymous proxies, location blockers, or browser-level privacy extensions (e.g. iCloud Private Relay, Tor, opted-out CMP signals) are excluded from per-market attribution.")));
}

// --- Campaign Performance table ---------------------------------------------

// §4.6 cross-link: MI campaign sections link into the Campaigns section
// (the dashboard pre-filtered via ?q=). Rendered only when the tenant has
// Campaigns enabled — MI unchanged for everyone else.
function ViewInCampaigns({
  q
}) {
  if (!hasTenantFeature('campaigns')) return null;
  return /*#__PURE__*/React.createElement("a", {
    href: `/Campaigns${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    className: "mi-report-meta",
    style: {
      marginLeft: 12
    }
  }, "View in Campaigns \u2192");
}
export function CampaignPerformanceTable({
  rows,
  title = 'Campaign Performance',
  emptyMessage = 'No paid campaigns in this window — connect an ad account under Campaigns › Settings, or let Autopilot draft the first flight.',
  market = ''
}) {
  if (!rows || rows.length === 0) {
    return /*#__PURE__*/React.createElement("section", {
      className: "mi-panel mi-panel-full"
    }, /*#__PURE__*/React.createElement("header", {
      className: "mi-panel-header"
    }, /*#__PURE__*/React.createElement("h2", null, title)), /*#__PURE__*/React.createElement("div", {
      className: "mi-panel-body mi-panel-empty"
    }, emptyMessage));
  }
  const buckets = bucketCampaignsByType(rows);
  const totals = totalsForRows(rows);
  const hasNonCtr = buckets.some(b => NON_CTR_TYPES.has(b.type));
  return /*#__PURE__*/React.createElement("section", {
    className: "mi-panel mi-panel-full"
  }, /*#__PURE__*/React.createElement("header", {
    className: "mi-panel-header"
  }, /*#__PURE__*/React.createElement("h2", null, title), /*#__PURE__*/React.createElement("div", {
    className: "mi-report-meta"
  }, "Campaigns: ", /*#__PURE__*/React.createElement("strong", null, fmt.int(totals.campaignCount)), /*#__PURE__*/React.createElement(ViewInCampaigns, {
    q: market
  }))), /*#__PURE__*/React.createElement("div", {
    className: "mi-panel-body"
  }, /*#__PURE__*/React.createElement("table", {
    className: "mi-report-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Type"), /*#__PURE__*/React.createElement("th", null, "Campaigns"), /*#__PURE__*/React.createElement("th", null, "Impressions"), /*#__PURE__*/React.createElement("th", null, "Video Views"), /*#__PURE__*/React.createElement("th", null, "Clicks"), /*#__PURE__*/React.createElement("th", null, "CTR"))), /*#__PURE__*/React.createElement("tbody", null, buckets.map(b => /*#__PURE__*/React.createElement("tr", {
    key: b.type
  }, /*#__PURE__*/React.createElement("td", null, b.type), /*#__PURE__*/React.createElement("td", null, fmt.int(b.campaignCount)), /*#__PURE__*/React.createElement("td", null, fmt.int(b.impressions)), /*#__PURE__*/React.createElement("td", null, fmt.int(b.videoViews)), /*#__PURE__*/React.createElement("td", null, fmt.int(b.clicks)), /*#__PURE__*/React.createElement("td", null, b.ctr === null ? '—' : fmt.pct(b.ctr)))), /*#__PURE__*/React.createElement("tr", {
    className: "mi-report-total"
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("strong", null, "TOTAL")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("strong", null, fmt.int(totals.campaignCount))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("strong", null, fmt.int(totals.impressions))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("strong", null, fmt.int(totals.videoViews))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("strong", null, fmt.int(totals.clicks))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("strong", null, fmt.pct(totals.ctr)))))), hasNonCtr && /*#__PURE__*/React.createElement("p", {
    className: "mi-report-note"
  }, "CTR is the unweighted mean of per-campaign per-day CTRs, excluding", ' ', Array.from(NON_CTR_TYPES).join(', '), " (video buys with no clickable inventory).")));
}

// --- Segment Campaigns ------------------------------------------------------

export function SegmentCampaignsTable({
  market,
  rows
}) {
  if (!SEGMENT_MARKETS.has(market)) return null;
  const seg = segmentBreakdown(rows);
  if (seg.items.length === 0) return null;
  return /*#__PURE__*/React.createElement("section", {
    className: "mi-panel mi-panel-full"
  }, /*#__PURE__*/React.createElement("header", {
    className: "mi-panel-header"
  }, /*#__PURE__*/React.createElement("h2", null, "Segment Campaigns"), /*#__PURE__*/React.createElement("div", {
    className: "mi-report-meta"
  }, "C1/C2/C3 audience tiers \u2014 Social only", /*#__PURE__*/React.createElement(ViewInCampaigns, {
    q: market
  }))), /*#__PURE__*/React.createElement("div", {
    className: "mi-panel-body"
  }, /*#__PURE__*/React.createElement("table", {
    className: "mi-report-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Campaign"), /*#__PURE__*/React.createElement("th", null, "Impressions"), /*#__PURE__*/React.createElement("th", null, "Clicks"), /*#__PURE__*/React.createElement("th", null, "CTR"))), /*#__PURE__*/React.createElement("tbody", null, seg.items.map(it => /*#__PURE__*/React.createElement("tr", {
    key: it.campaign
  }, /*#__PURE__*/React.createElement("td", null, hasTenantFeature('campaigns') ? /*#__PURE__*/React.createElement("a", {
    href: `/Campaigns?q=${encodeURIComponent(it.campaign)}`
  }, prettifyCampaignName(it.campaign)) : prettifyCampaignName(it.campaign)), /*#__PURE__*/React.createElement("td", null, fmt.int(it.impressions)), /*#__PURE__*/React.createElement("td", null, fmt.int(it.clicks)), /*#__PURE__*/React.createElement("td", null, fmt.pct(it.ctr)))), /*#__PURE__*/React.createElement("tr", {
    className: "mi-report-total"
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("strong", null, "TOTAL (", fmt.int(seg.total.campaignCount), " campaigns)")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("strong", null, fmt.int(seg.total.impressions))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("strong", null, fmt.int(seg.total.clicks))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("strong", null, fmt.pct(seg.total.ctr)))))), /*#__PURE__*/React.createElement("p", {
    className: "mi-report-note"
  }, "CTR is the unweighted mean of per-campaign per-day CTRs.")));
}

// --- Tracked Website Usage --------------------------------------------------

export function TrackedWebsiteUsage({
  place,
  prevPlace,
  placeZips,
  cityZips,
  marketName
}) {
  const hasAny = place || placeZips && placeZips.length || cityZips && cityZips.length;
  if (!hasAny) return null;
  const pps = place && place.sessions > 0 ? place.views / place.sessions : 0;
  const prevPps = prevPlace && prevPlace.sessions > 0 ? prevPlace.views / prevPlace.sessions : 0;
  const placeZipTotal = (placeZips || []).reduce((s, z) => s + (z.views || 0), 0);
  const hasPrev = Boolean(prevPlace);
  const row = (label, current, previous, valueFmt = fmt.int, deltaFn = deltaCell) => /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, label), /*#__PURE__*/React.createElement("td", null, valueFmt(current)), hasPrev && /*#__PURE__*/React.createElement("td", null, valueFmt(previous)), hasPrev && /*#__PURE__*/React.createElement("td", null, deltaFn(current, previous)));
  return /*#__PURE__*/React.createElement("section", {
    className: "mi-panel mi-panel-full"
  }, /*#__PURE__*/React.createElement("header", {
    className: "mi-panel-header"
  }, /*#__PURE__*/React.createElement("h2", null, "Tracked Website Usage")), /*#__PURE__*/React.createElement("div", {
    className: "mi-panel-body"
  }, place ? /*#__PURE__*/React.createElement("table", {
    className: "mi-report-table mi-report-table-keyvalue"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Metric"), /*#__PURE__*/React.createElement("th", null, "Value"), hasPrev && /*#__PURE__*/React.createElement("th", null, "Previous"), hasPrev && /*#__PURE__*/React.createElement("th", null, "\u0394"))), /*#__PURE__*/React.createElement("tbody", null, row('Views', place.views, prevPlace && prevPlace.views), row('Sessions', place.sessions, prevPlace && prevPlace.sessions), row('Unique Users', place.uniqueUsers, prevPlace && prevPlace.uniqueUsers), row('Pages/Session', pps, prevPps, fmt.dec2, (c, p) => deltaCellPoints(c, p, 'pp')), row('Avg Session (sec)', place.avgTimeSpan, prevPlace && prevPlace.avgTimeSpan, fmt.dec1, (c, p) => deltaCellPoints(c, p, 's')), row('Conversions', place.conversions, prevPlace && prevPlace.conversions))) : /*#__PURE__*/React.createElement("p", {
    className: "mi-report-note"
  }, "No place-level rollup for this market."), placeZips && placeZips.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h3", {
    className: "mi-report-subhead"
  }, "Top zips within targeting footprint (", placeZips.length, " zips, ", fmt.int(placeZipTotal), " views)"), /*#__PURE__*/React.createElement(ZipTable, {
    rows: placeZips
  })), cityZips && cityZips.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h3", {
    className: "mi-report-subhead"
  }, "Top zips matched by ", /*#__PURE__*/React.createElement("code", null, "city='", marketName, "'"), " (IP-resolved subset)"), /*#__PURE__*/React.createElement(ZipTable, {
    rows: cityZips
  }))));
}
function ZipTable({
  rows
}) {
  return /*#__PURE__*/React.createElement("table", {
    className: "mi-report-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Zip"), /*#__PURE__*/React.createElement("th", null, "City"), /*#__PURE__*/React.createElement("th", null, "Views"), /*#__PURE__*/React.createElement("th", null, "Sessions"), /*#__PURE__*/React.createElement("th", null, "Users"), /*#__PURE__*/React.createElement("th", null, "Pages/Sess"), /*#__PURE__*/React.createElement("th", null, "Avg Sec"), /*#__PURE__*/React.createElement("th", null, "Conv"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((z, i) => {
    const pps = z.sessions > 0 ? z.views / z.sessions : 0;
    return /*#__PURE__*/React.createElement("tr", {
      key: `${z.postalCode || '—'}-${i}`
    }, /*#__PURE__*/React.createElement("td", null, z.postalCode || '—'), /*#__PURE__*/React.createElement("td", null, z.city || '—'), /*#__PURE__*/React.createElement("td", null, fmt.int(z.views)), /*#__PURE__*/React.createElement("td", null, fmt.int(z.sessions)), /*#__PURE__*/React.createElement("td", null, fmt.int(z.uniqueUsers)), /*#__PURE__*/React.createElement("td", null, fmt.dec2(pps)), /*#__PURE__*/React.createElement("td", null, fmt.dec1(z.avgTimeSpan)), /*#__PURE__*/React.createElement("td", null, fmt.int(z.conversions)));
  })));
}

// --- Top Products (per-market) ----------------------------------------------

export const TopProductsTable = withRouter(function TopProductsTable({
  rows,
  history
}) {
  if (!rows || rows.length === 0) return null;
  return /*#__PURE__*/React.createElement("section", {
    className: "mi-panel mi-panel-full"
  }, /*#__PURE__*/React.createElement("header", {
    className: "mi-panel-header"
  }, /*#__PURE__*/React.createElement("h2", null, "Top Products"), /*#__PURE__*/React.createElement("div", {
    className: "mi-report-meta"
  }, "top ", rows.length, " by views within this market's footprint")), /*#__PURE__*/React.createElement("div", {
    className: "mi-panel-body"
  }, /*#__PURE__*/React.createElement("table", {
    className: "mi-report-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Product"), /*#__PURE__*/React.createElement("th", null, "Views"), /*#__PURE__*/React.createElement("th", null, "Sessions"), /*#__PURE__*/React.createElement("th", null, "Users"), /*#__PURE__*/React.createElement("th", null, "Engagements"), /*#__PURE__*/React.createElement("th", null, "Conv"), /*#__PURE__*/React.createElement("th", null, "Conv $"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(p => /*#__PURE__*/React.createElement("tr", _extends({
    key: p.productId
  }, productRowProps(history, p)), /*#__PURE__*/React.createElement("td", null, clean(p.title) || p.productId), /*#__PURE__*/React.createElement("td", null, fmt.int(p.views)), /*#__PURE__*/React.createElement("td", null, fmt.int(p.sessions)), /*#__PURE__*/React.createElement("td", null, fmt.int(p.uniqueUsers)), /*#__PURE__*/React.createElement("td", null, fmt.int(p.engagements)), /*#__PURE__*/React.createElement("td", null, fmt.int(p.conversions)), /*#__PURE__*/React.createElement("td", null, fmt.money0(p.conversionsPriceValue))))))));
});

// --- Top Categories (per-market) --------------------------------------------

export function TopCategoriesTable({
  rows
}) {
  if (!rows || rows.length === 0) return null;
  return /*#__PURE__*/React.createElement("section", {
    className: "mi-panel mi-panel-full"
  }, /*#__PURE__*/React.createElement("header", {
    className: "mi-panel-header"
  }, /*#__PURE__*/React.createElement("h2", null, "Top Categories"), /*#__PURE__*/React.createElement("div", {
    className: "mi-report-meta"
  }, "rolled up from products within this market's footprint")), /*#__PURE__*/React.createElement("div", {
    className: "mi-panel-body"
  }, /*#__PURE__*/React.createElement("table", {
    className: "mi-report-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Category"), /*#__PURE__*/React.createElement("th", null, "Products"), /*#__PURE__*/React.createElement("th", null, "Views"), /*#__PURE__*/React.createElement("th", null, "Sessions"), /*#__PURE__*/React.createElement("th", null, "Users"), /*#__PURE__*/React.createElement("th", null, "Conv"), /*#__PURE__*/React.createElement("th", null, "Conv $"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(c => /*#__PURE__*/React.createElement("tr", {
    key: c.category
  }, /*#__PURE__*/React.createElement("td", null, c.category), /*#__PURE__*/React.createElement("td", null, fmt.int(c.products)), /*#__PURE__*/React.createElement("td", null, fmt.int(c.views)), /*#__PURE__*/React.createElement("td", null, fmt.int(c.sessions)), /*#__PURE__*/React.createElement("td", null, fmt.int(c.uniqueUsers)), /*#__PURE__*/React.createElement("td", null, fmt.int(c.conversions)), /*#__PURE__*/React.createElement("td", null, fmt.money0(c.conversionsPriceValue))))))));
}

// --- Analysis ---------------------------------------------------------------

export function AnalysisRecommendations({
  market,
  marketEntry,
  allMarketData
}) {
  const {
    callouts,
    recommendations
  } = deriveAnalysis(market, marketEntry, allMarketData);
  if (callouts.length === 0 && recommendations.length === 0) return null;
  return /*#__PURE__*/React.createElement("section", {
    className: "mi-panel mi-panel-full"
  }, /*#__PURE__*/React.createElement("header", {
    className: "mi-panel-header"
  }, /*#__PURE__*/React.createElement("h2", null, "Analysis")), /*#__PURE__*/React.createElement("div", {
    className: "mi-panel-body"
  }, /*#__PURE__*/React.createElement("ul", {
    className: "mi-analysis-list"
  }, callouts.map((c, i) => /*#__PURE__*/React.createElement("li", {
    key: `c${i}`
  }, renderInlineMd(c))), recommendations.map((r, i) => /*#__PURE__*/React.createElement("li", {
    key: `r${i}`,
    className: "mi-analysis-rec"
  }, /*#__PURE__*/React.createElement("em", null, "Recommendation:"), " ", renderInlineMd(r))))));
}

// --- Cluster Top Products by Performance -----------------------------------

export const ClusterTopProductsPerformance = withRouter(function ClusterTopProductsPerformance({
  rows,
  history
}) {
  if (!rows || rows.length === 0) return null;
  return /*#__PURE__*/React.createElement("section", {
    className: "mi-panel mi-panel-full"
  }, /*#__PURE__*/React.createElement("header", {
    className: "mi-panel-header"
  }, /*#__PURE__*/React.createElement("h2", null, "Top Products by Performance"), /*#__PURE__*/React.createElement("div", {
    className: "mi-report-meta"
  }, "cluster-wide \u2014 from product_performance_stats")), /*#__PURE__*/React.createElement("div", {
    className: "mi-panel-body"
  }, /*#__PURE__*/React.createElement("table", {
    className: "mi-report-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Product"), /*#__PURE__*/React.createElement("th", null, "Views"), /*#__PURE__*/React.createElement("th", null, "Users"), /*#__PURE__*/React.createElement("th", null, "Engagements"), /*#__PURE__*/React.createElement("th", null, "Contacts Interested"), /*#__PURE__*/React.createElement("th", null, "Sales Qty"), /*#__PURE__*/React.createElement("th", null, "Conv"), /*#__PURE__*/React.createElement("th", null, "Conv $"), /*#__PURE__*/React.createElement("th", null, "Forecast Views"), /*#__PURE__*/React.createElement("th", null, "Forecast Sales"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(p => {
    const title = resolveTitle(p.link, p.title);
    return /*#__PURE__*/React.createElement("tr", _extends({
      key: p.productId
    }, productRowProps(history, {
      ...p,
      title
    })), /*#__PURE__*/React.createElement("td", null, title || p.productId), /*#__PURE__*/React.createElement("td", null, fmt.int(p.views)), /*#__PURE__*/React.createElement("td", null, fmt.int(p.uniqueUsers)), /*#__PURE__*/React.createElement("td", null, fmt.int(p.engagements)), /*#__PURE__*/React.createElement("td", null, fmt.int(p.contactsInterested)), /*#__PURE__*/React.createElement("td", null, fmt.int(p.salesQuantity)), /*#__PURE__*/React.createElement("td", null, fmt.int(p.conversions)), /*#__PURE__*/React.createElement("td", null, fmt.money0(p.conversionsPriceValue)), /*#__PURE__*/React.createElement("td", null, fmt.int(p.forecastViews)), /*#__PURE__*/React.createElement("td", null, fmt.int(p.forecastSalesQuantity)));
  })))));
});